import { Form, Link, usePage } from "@inertiajs/react"
import {
  Check,
  ChevronDown,
  Coins,
  Gift,
  Loader2,
  Lock,
  Package,
  PartyPopper,
  Sparkles,
  Users,
} from "lucide-react"
import { useRef, useState } from "react"
import type { MutableRefObject, ReactNode } from "react"
import { useTranslation } from "react-i18next"

import {
  type RedeemProfileAddress,
  RedeemShipTo,
} from "@/components/coins/redeem-ship-to"
import type { OnboardingAddressErrors } from "@/components/onboarding/onboarding-address-fieldset"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { Progress } from "@/components/ui/progress"
import { useScrollOverflow } from "@/hooks/use-scroll-overflow"
import {
  creatorMissionsPath,
  creatorRedeemRewardPath,
  creatorRedemptionsPath,
} from "@/routes"

import type { RewardCardReward } from "./reward-card"

/**
 * OKS-557 — §20 reward redeem bottom-sheet (UX Spec §1-§6).
 *
 * A single state-driven `ui/drawer` sheet that evolves the shipped OKS-60
 * `redeem-alert-dialog`. It reuses the same POST target
 * (`creatorRedeemRewardPath`) and the same Inertia error contract
 * (`errors.base` → inline destructive `Alert`) — the atomic debit lives
 * entirely in `Redemptions::Claim` server-side (untouched here).
 *
 * # Five states (UX Spec §3), derived from props (no fragile machine)
 *
 *   - **Confirm** — available + in stock + affordable + no pending:
 *     gold coin-ledger card (Cost · Your balance · emphasized
 *     Balance-after) + Confirm/Cancel. This is the decision anchor.
 *   - **Redeeming** — Confirm tapped, Inertia `<Form>` `processing`:
 *     in-button spinner, disabled, `aria-busy`; the Drawer refuses
 *     dismissal mid-flight (the render-prop mirrors `processing` into a
 *     shared ref the `onOpenChange` guard reads). The ledger stays
 *     visible (not a full-screen spinner).
 *   - **Redeemed** — post-success: gold celebration + order reference
 *     (`Redemption#id` via `flash.redeemed_redemption_id`) + tracking
 *     copy. CTAs: View my redemptions / Done.
 *   - **Can't-afford** — available but balance < cost: amber
 *     "🪙 N coins short" hero + progress (balance/cost) + "~M missions
 *     to earn enough" + full-accent Find-missions CTA (→ missions).
 *   - **Edge/locked** — locked reward: "🪙 N coins to go" + progress +
 *     "~M missions to unlock" + Find-missions CTA. Plus out-of-stock
 *     (Confirm → disabled "Out of stock") and already-pending
 *     ("already requested" + link to my redemptions) sub-states.
 *
 * # In-sheet Redeemed (PRD OQ-6)
 *
 * The Confirm `<Form>` posts with `preserveState: true` so the sheet
 * stays mounted across the 303 success redirect (the OKS-60 dialog
 * auto-closed instead). On success we flip to the Redeemed state and
 * read the order ref from `usePage().flash.redeemed_redemption_id`. The
 * existing sonner flash toast still fires on re-render (secondary
 * feedback per UX Spec §4).
 *
 * # Mission estimate (PRD OQ-2 — honest, not the mock's ~125)
 *
 * `M = ceil(coinsShort / coinsBasePerMission)` using the current
 * brand's real per-mission base (default 10). Excludes the on-time
 * bonus (not guaranteed) so the estimate never under-promises.
 *
 * # Footer pinning + above-the-fold body (OKS-645, memory
 * crew_vaul_sheet_peek_footer_pinning)
 *
 * The inner column is capped at `max-h-[80dvh]` (on OUR div, not the shadcn
 * `Drawer` primitive — `dvh` also keeps the footer clear of the mobile
 * Safari URL-bar that plain `vh` measures behind). Inside it: a `shrink-0`
 * header, a `flex-1` internally-scrollable `SheetScrollBody`, and a
 * `shrink-0` `DrawerFooter` OUTSIDE the scroll region. So the essential
 * decision info (ledger / coins-short hero + `Progress`) sits at the top of
 * the body and the CTA stays pinned — both visible on open with no gesture.
 * When the body genuinely overflows the smallest phone, `SheetScrollBody`
 * shows a fit-first "more below" cue (`useScrollOverflow`); the only content
 * that can fall behind it is the trailing "~M missions" estimate line.
 *
 * # Brand isolation
 *
 * All values are server-resolved for `Current.brand` (the reward, the
 * balance, `redeemed_count`, `coins_base_per_mission`). The POST URL is
 * brand-implicit; a cross-brand probe 404s server-side before the
 * policy/service (OKS-6 fail-closed).
 *
 * NO shadcn `Form`/`FormField`/`FormMessage`, NO RHF/zod — Crew rule.
 */

interface RedeemSheetProps {
  /** The reward whose sheet is open; `null` closes the sheet. */
  reward: RewardCardReward | null
  /** Current spendable coin balance (server SUM). */
  balance: number
  brandName: string
  /** Current brand's per-mission coin award (default 10) — PRD OQ-2. */
  coinsBasePerMission: number
  /**
   * OKS-985 — the creator's OWN profile address, prefilling the gear claim's
   * Ship-to block. `null` when they have none, which expands the fieldset with
   * its lead line rather than rendering a blank block.
   */
  profileAddress: RedeemProfileAddress | null
  /** Parent resets `selectedReward` to `null`. */
  onClose: () => void
}

type BaseState =
  | "confirm"
  | "cant_afford"
  | "locked"
  | "out_of_stock"
  | "already_pending"

const KIND_ICON: Record<string, typeof Gift> = {
  product: Package,
  experience: Sparkles,
  custom: Gift,
}

function baseStateFor(reward: RewardCardReward): BaseState {
  if (reward.state === "locked") return "locked"
  if (reward.pending) return "already_pending"
  if (reward.out_of_stock) return "out_of_stock"
  if (!reward.affordable) return "cant_afford"
  return "confirm"
}

export function RedeemSheet({ reward, onClose, ...rest }: RedeemSheetProps) {
  const open = reward !== null
  // Mirrors the Confirm form's `processing` so the Drawer can refuse
  // dismissal mid-flight (UX Spec §3 — "sheet onOpenChange locked while
  // processing"). A ref (not state) so mirroring it from the Form
  // render-prop doesn't need a setState-in-effect.
  const submittingRef = useRef(false)

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next && !submittingRef.current) onClose()
      }}
    >
      <DrawerContent data-testid="redeem-sheet">
        {reward && (
          // Keyed on reward id so switching rewards remounts the inner
          // sheet — the `justRedeemed` flag resets naturally without a
          // setState-in-effect (eslint react-hooks/set-state-in-effect).
          <SheetInner
            key={reward.id}
            reward={reward}
            onClose={onClose}
            submittingRef={submittingRef}
            {...rest}
          />
        )}
      </DrawerContent>
    </Drawer>
  )
}

function SheetInner({
  reward,
  balance,
  brandName,
  coinsBasePerMission,
  profileAddress,
  onClose,
  submittingRef,
}: RedeemSheetProps & {
  reward: RewardCardReward
  submittingRef: MutableRefObject<boolean>
}) {
  const { t } = useTranslation()
  const { flash } = usePage()

  // Local success flag, flipped by the Form's onSuccess. Fresh per mount
  // (see the `key` on this component) so a prior redeem never leaks its
  // Redeemed celebration onto the next reward.
  const [justRedeemed, setJustRedeemed] = useState(false)

  const base = baseStateFor(reward)
  const cost = reward.coin_cost
  const balanceAfter = balance - cost
  const coinsShort = Math.max(0, cost - balance)
  const progressValue =
    cost > 0 ? Math.min(100, Math.round((balance / cost) * 100)) : 100
  // Guard divide-by-zero when a brand configures a 0-coin mission base.
  const missionsEstimate =
    coinsShort > 0 && coinsBasePerMission > 0
      ? Math.ceil(coinsShort / coinsBasePerMission)
      : null
  const orderRef = flash?.redeemed_redemption_id ?? null

  // OKS-645 — cap the inner column at 80dvh (our own div, never the shadcn
  // primitive) so the header, top-of-body essential info, and pinned footer
  // all clear the fold on 375/402px without a gesture; overflow is absorbed
  // by the scrollable `SheetScrollBody`, not clipped.
  return (
    <div className="mx-auto flex max-h-[80dvh] w-full max-w-md flex-col">
      <SheetHeader reward={reward} t={t} />

      {justRedeemed ? (
        <RedeemedBody orderRef={orderRef} onClose={onClose} t={t} />
      ) : base === "confirm" ? (
        <ConfirmBody
          profileAddress={profileAddress}
          reward={reward}
          balance={balance}
          balanceAfter={balanceAfter}
          brandName={brandName}
          onSuccess={() => setJustRedeemed(true)}
          onClose={onClose}
          submittingRef={submittingRef}
          t={t}
        />
      ) : base === "cant_afford" || base === "locked" ? (
        <CoinsShortBody
          variant={base}
          coinsShort={coinsShort}
          progressValue={progressValue}
          missionsEstimate={missionsEstimate}
          onClose={onClose}
          t={t}
        />
      ) : base === "out_of_stock" ? (
        <OutOfStockBody onClose={onClose} t={t} />
      ) : (
        <AlreadyPendingBody onClose={onClose} t={t} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Header — cover + name + "👥 N redeemed" meta (shared across states).
// ---------------------------------------------------------------------------

function SheetHeader({
  reward,
  t,
}: {
  reward: RewardCardReward
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  const [errored, setErrored] = useState(false)
  const KindIcon = KIND_ICON[reward.kind] ?? Gift
  const showImage = Boolean(reward.cover_image_url) && !errored

  return (
    <DrawerHeader className="flex shrink-0 flex-row items-center gap-3 text-left">
      {showImage ? (
        <img
          src={reward.cover_image_url!}
          alt=""
          loading="lazy"
          onError={() => setErrored(true)}
          data-testid="redeem-sheet-cover"
          className="size-16 shrink-0 rounded-lg object-cover"
        />
      ) : (
        <div
          aria-hidden="true"
          data-testid="redeem-sheet-cover-fallback"
          className="bg-muted text-muted-foreground flex size-16 shrink-0 items-center justify-center rounded-lg"
        >
          <KindIcon className="size-6" />
        </div>
      )}

      <div className="flex min-w-0 flex-col gap-1">
        <DrawerTitle
          className="line-clamp-2 text-base"
          data-testid="redeem-sheet-title"
        >
          {reward.title}
        </DrawerTitle>
        <DrawerDescription
          className="inline-flex items-center gap-1 text-xs"
          data-testid="redeem-sheet-redeemed-count"
        >
          <Users className="size-3" aria-hidden="true" />
          {t("rewards.creator.sheet.redeemed_count", {
            count: reward.redeemed_count,
          })}
        </DrawerDescription>
      </div>
    </DrawerHeader>
  )
}

// ---------------------------------------------------------------------------
// SheetScrollBody — the internally-scrollable body region shared by every
// state (OKS-645). `flex-1 min-h-0` so it absorbs any overflow while the
// sibling header/footer stay pinned; the essential info its children render
// first is therefore always above the fold on open. A fit-first "more below"
// cue appears only on genuine overflow and hides once scrolled to the end.
// ---------------------------------------------------------------------------

function SheetScrollBody({
  children,
  t,
}: {
  children: ReactNode
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const { overflowing, atEnd } = useScrollOverflow(scrollRef)
  const showCue = overflowing && !atEnd

  // The wrapper is itself a flex column (a flex item of the sheet column), so
  // the scroll child sizes via `flex-1 min-h-0` — NOT a `height:100%` that
  // wouldn't resolve against a flex-grown parent and would let tall content
  // spill below the fold instead of clipping + scrolling.
  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-2"
      >
        {children}
      </div>
      {showCue && (
        // Non-color affordance (UX Spec §6): a chevron shape + an sr-only
        // label, not a color-only signal. `pointer-events-none` so the fade
        // never intercepts the scroll it hints at.
        <div
          data-testid="redeem-sheet-more-cue"
          className="from-background pointer-events-none absolute inset-x-0 bottom-0 flex justify-center bg-gradient-to-t to-transparent pt-6 pb-1"
        >
          <ChevronDown
            className="text-muted-foreground size-5"
            aria-hidden="true"
          />
          <span className="sr-only">
            {t("rewards.creator.sheet.more_below")}
          </span>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Confirm — gold coin-ledger + Confirm/Cancel; swaps to in-button spinner
// while the Inertia <Form> is processing (Redeeming state).
// ---------------------------------------------------------------------------

function ConfirmBody({
  reward,
  balance,
  balanceAfter,
  brandName,
  profileAddress,
  onSuccess,
  onClose,
  submittingRef,
  t,
}: {
  reward: RewardCardReward
  balance: number
  balanceAfter: number
  brandName: string
  profileAddress: RedeemProfileAddress | null
  onSuccess: () => void
  onClose: () => void
  submittingRef: MutableRefObject<boolean>
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  return (
    <Form
      action={creatorRedeemRewardPath(reward.id)}
      method="post"
      // OKS-645 — flex column so the ledger scrolls and the footer pins
      // within the Form (the Form is the column's flex-1 region).
      className="flex min-h-0 flex-1 flex-col"
      // preserveState keeps the sheet mounted across the 303 success
      // redirect so we can show the in-sheet Redeemed state (PRD OQ-6);
      // preserveScroll avoids a jump on the catalogue behind it.
      options={{ preserveScroll: true, preserveState: true }}
      onSuccess={onSuccess}
    >
      {({ processing, errors }) => {
        // Mirror processing into the shared ref so the Drawer refuses
        // dismissal while the redeem POST is in flight (UX Spec §3).
        submittingRef.current = processing
        return (
          <>
            <SheetScrollBody t={t}>
              {/* Gold coin-ledger card — the decision anchor (UX Spec §2). */}
              <div
                data-testid="redeem-sheet-ledger"
                className="rounded-xl border border-amber-200 bg-gradient-to-b from-amber-50 to-orange-50 p-4"
              >
                <LedgerRow
                  label={t("rewards.creator.sheet.ledger.cost")}
                  value={t("rewards.creator.sheet.coins", {
                    count: reward.coin_cost,
                  })}
                  testId="redeem-sheet-ledger-cost"
                />
                <LedgerRow
                  label={t("rewards.creator.sheet.ledger.balance")}
                  value={t("rewards.creator.sheet.coins", { count: balance })}
                  testId="redeem-sheet-ledger-balance"
                />
                <div className="my-2 border-t border-amber-200/70" />
                {/* Balance-after — emphasized, not color-only (label + glyph
                  + weight) per UX Spec §6 accessibility. */}
                <div
                  className="flex items-center justify-between"
                  data-testid="redeem-sheet-ledger-balance-after"
                >
                  <span className="text-sm font-medium text-amber-900">
                    {t("rewards.creator.sheet.ledger.balance_after")}
                  </span>
                  <span className="inline-flex items-center gap-1 text-lg font-bold text-amber-900 tabular-nums">
                    <Coins className="size-4" aria-hidden="true" />
                    {t("rewards.creator.sheet.coins", { count: balanceAfter })}
                  </span>
                </div>
              </div>

              {/* OKS-985 — the destination, for `gear` only (PRD R4).
                  BELOW the ledger: cost is the first decision, destination
                  the second (UX Spec §2.1). `experiences` / `perks` get
                  today's sheet byte-for-byte — no block, no params, nothing
                  posted. */}
              {reward.category === "gear" && (
                <RedeemShipTo
                  profileAddress={profileAddress}
                  errors={errors as OnboardingAddressErrors}
                />
              )}

              <p className="text-muted-foreground mt-3 text-sm">
                {t("rewards.creator.sheet.fulfillment", { brand: brandName })}
              </p>

              {errors.base && (
                <Alert
                  variant="destructive"
                  role="alert"
                  data-testid="redeem-sheet-error"
                  className="mt-3"
                >
                  <AlertDescription>{errors.base}</AlertDescription>
                </Alert>
              )}
            </SheetScrollBody>

            <DrawerFooter className="shrink-0">
              <Button
                type="submit"
                disabled={processing}
                aria-busy={processing}
                data-testid="redeem-sheet-confirm"
              >
                {processing && (
                  <Loader2
                    aria-hidden="true"
                    className="mr-2 size-4 animate-spin"
                  />
                )}
                {processing
                  ? t("rewards.creator.sheet.redeeming")
                  : t("rewards.creator.sheet.confirm", {
                      count: reward.coin_cost,
                    })}
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={processing}
                onClick={onClose}
                data-testid="redeem-sheet-cancel"
              >
                {t("rewards.creator.sheet.cancel")}
              </Button>
            </DrawerFooter>
          </>
        )
      }}
    </Form>
  )
}

function LedgerRow({
  label,
  value,
  testId,
}: {
  label: string
  value: string
  testId: string
}) {
  return (
    <div
      className="flex items-center justify-between py-0.5"
      data-testid={testId}
    >
      <span className="text-sm text-amber-800">{label}</span>
      <span className="text-sm font-semibold text-amber-900 tabular-nums">
        {value}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Redeemed — gold celebration + order ref + tracking copy.
// ---------------------------------------------------------------------------

function RedeemedBody({
  orderRef,
  onClose,
  t,
}: {
  orderRef: number | null
  onClose: () => void
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  return (
    <>
      <SheetScrollBody t={t}>
        <div
          className="flex flex-col items-center gap-2 text-center"
          data-testid="redeem-sheet-redeemed"
        >
          {/* Decorative celebration — respects reduced-motion (no keyframe
              animation used, so nothing to disable). aria-hidden per §6. */}
          <div
            aria-hidden="true"
            className="flex size-14 items-center justify-center rounded-full bg-gradient-to-b from-amber-400 to-orange-500 text-white"
          >
            <PartyPopper className="size-7" />
          </div>
          <p className="text-lg font-semibold">
            {t("rewards.creator.sheet.redeemed_title")}
          </p>
          {orderRef !== null && (
            <p
              className="text-muted-foreground text-sm"
              data-testid="redeem-sheet-order-ref"
            >
              {t("rewards.creator.sheet.order_ref", { ref: orderRef })}
            </p>
          )}
          <p className="text-muted-foreground text-sm">
            {t("rewards.creator.sheet.tracking")}
          </p>
        </div>
      </SheetScrollBody>

      <DrawerFooter className="shrink-0">
        <Button asChild data-testid="redeem-sheet-view-redemptions">
          <Link href={creatorRedemptionsPath()}>
            {t("rewards.creator.sheet.view_redemptions")}
          </Link>
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          data-testid="redeem-sheet-done"
        >
          {t("rewards.creator.sheet.done")}
        </Button>
      </DrawerFooter>
    </>
  )
}

// ---------------------------------------------------------------------------
// Can't-afford / locked — amber coins-short hero + progress + missions
// estimate + Find-missions CTA.
// ---------------------------------------------------------------------------

function CoinsShortBody({
  variant,
  coinsShort,
  progressValue,
  missionsEstimate,
  onClose,
  t,
}: {
  variant: "cant_afford" | "locked"
  coinsShort: number
  progressValue: number
  missionsEstimate: number | null
  onClose: () => void
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  const heroKey =
    variant === "locked"
      ? "rewards.creator.sheet.coins_to_go"
      : "rewards.creator.sheet.coins_short"
  const missionsKey =
    variant === "locked"
      ? "rewards.creator.sheet.missions_to_unlock"
      : "rewards.creator.sheet.missions_to_earn"

  return (
    <>
      <SheetScrollBody t={t}>
        {/* Hero card — headline + Progress are the anchor and render first,
            so they clear the fold on open; only the trailing missions
            estimate may fall behind the "more below" cue (UX Spec §2). */}
        <div
          data-testid="redeem-sheet-coins-short"
          className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-center"
        >
          <p className="inline-flex items-center gap-1.5 text-xl font-bold text-amber-900">
            <Coins className="size-5" aria-hidden="true" />
            {t(heroKey, { count: coinsShort })}
          </p>
          <Progress
            value={progressValue}
            data-testid="redeem-sheet-progress"
            aria-label={t("rewards.creator.sheet.progress_aria", {
              value: progressValue,
            })}
            className="mt-3 bg-amber-200 [&_[data-slot=progress-indicator]]:bg-amber-500"
          />
          {missionsEstimate !== null && (
            <p
              className="mt-2 text-sm text-amber-800"
              data-testid="redeem-sheet-missions-estimate"
            >
              {t(missionsKey, { count: missionsEstimate })}
            </p>
          )}
        </div>
      </SheetScrollBody>

      <DrawerFooter className="shrink-0">
        <Button asChild data-testid="redeem-sheet-find-missions">
          <Link href={creatorMissionsPath()}>
            {t("rewards.creator.sheet.find_missions")}
          </Link>
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          data-testid="redeem-sheet-cancel"
        >
          {t("rewards.creator.sheet.cancel")}
        </Button>
      </DrawerFooter>
    </>
  )
}

// ---------------------------------------------------------------------------
// Out-of-stock — Confirm replaced by a disabled "Out of stock" state.
// ---------------------------------------------------------------------------

function OutOfStockBody({
  onClose,
  t,
}: {
  onClose: () => void
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  return (
    <>
      <SheetScrollBody t={t}>
        <div data-testid="redeem-sheet-out-of-stock">
          <Alert role="status">
            <Lock className="size-4" aria-hidden="true" />
            <AlertDescription>
              {t("rewards.creator.sheet.out_of_stock")}
            </AlertDescription>
          </Alert>
        </div>
      </SheetScrollBody>
      <DrawerFooter className="shrink-0">
        <Button type="button" disabled data-testid="redeem-sheet-confirm">
          {t("rewards.creator.sheet.out_of_stock_cta")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          data-testid="redeem-sheet-cancel"
        >
          {t("rewards.creator.sheet.cancel")}
        </Button>
      </DrawerFooter>
    </>
  )
}

// ---------------------------------------------------------------------------
// Already-pending — a live redemption exists; block + link to history.
// ---------------------------------------------------------------------------

function AlreadyPendingBody({
  onClose,
  t,
}: {
  onClose: () => void
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  return (
    <>
      <SheetScrollBody t={t}>
        <div data-testid="redeem-sheet-already-pending">
          <Alert role="status">
            <Check className="size-4" aria-hidden="true" />
            <AlertDescription>
              {t("rewards.creator.sheet.already_pending")}
            </AlertDescription>
          </Alert>
        </div>
      </SheetScrollBody>
      <DrawerFooter className="shrink-0">
        <Button asChild data-testid="redeem-sheet-view-redemptions">
          <Link href={creatorRedemptionsPath()}>
            {t("rewards.creator.sheet.view_redemptions")}
          </Link>
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          data-testid="redeem-sheet-cancel"
        >
          {t("rewards.creator.sheet.cancel")}
        </Button>
      </DrawerFooter>
    </>
  )
}
